import React, { useMemo } from "react";
import { Spinner } from "@/shared/components/common/Spinner/Spinner";
import { Button } from "@/shared/components/common/Button/Button";
import { DealsAnalyticsContext, DepartmentPlanDeal } from "../../../model/dashboard.types";
import {
  DEAL_COLUMNS,
  DEAL_GROUP_OPTIONS,
  DealColumnId,
  REQUIRED_VISIBLE_COLUMNS,
  formatDealMoney,
  formatDealMarginality,
} from "./dealsDetailsColumns";
import { useDealsDetailsView } from "./useDealsDetailsView";
import { exportDealsDetailsToExcel } from "./exportDealsDetailsToExcel";
import css from "./DealsDetailsView.module.css";

type DealsDetailsViewProps = {
  loading: boolean;
  deals: DepartmentPlanDeal[] | undefined;
  exportTitle?: string;
  analytics?: DealsAnalyticsContext;
};

function renderDealCell(columnId: DealColumnId, deal: DepartmentPlanDeal): React.ReactNode {
  switch (columnId) {
    case "deal":
      return (
        <a href={deal.link} target="_blank" rel="noopener noreferrer">
          Открыть
        </a>
      );
    case "client":
      return deal.client ?? "—";
    case "opportunity":
      return formatDealMoney(deal.opportunity);
    case "profit":
      return formatDealMoney(deal.profit);
    case "marginality":
      return formatDealMarginality(deal.marginality);
    case "actual_paid":
      return formatDealMoney(deal.actual_paid);
    case "stage_id":
      return deal.stage_id;
    case "responsible":
      return deal.responsible;
    default:
      return "—";
  }
}

function DealsDetailsView({ loading, deals, exportTitle = "Сделки" }: DealsDetailsViewProps) {
  const { visibleColumns, groupBy, rows, toggleColumn, toggleGroup, groupTotals } =
    useDealsDetailsView(deals);

  const columns = useMemo(
    () => DEAL_COLUMNS.filter((column) => visibleColumns.includes(column.id)),
    [visibleColumns]
  );

  const groupOptionLabel = (groupKey: "client" | "responsible") =>
    DEAL_GROUP_OPTIONS.find((option) => option.id === groupKey)?.label ?? groupKey;

  const handleExportExcel = () => {
    exportDealsDetailsToExcel({
      title: exportTitle,
      columns: visibleColumns,
      rows,
    });
  };

  if (loading) {
    return (
      <div className={css.emptyCell}>
        <span>Получение данных </span>
        <Spinner />
      </div>
    );
  }

  if (!deals?.length) {
    return <div className={css.emptyCell}>Нет закрытых сделок за выбранный период</div>;
  }

  return (
    <>
      <div className={css.toolbar}>
        <div className={css.toolbarBlock}>
          <div className={css.toolbarTitle}>Столбцы</div>
          <div className={css.checkboxList}>
            {DEAL_COLUMNS.map((column) => {
              const isRequired = REQUIRED_VISIBLE_COLUMNS.includes(column.id);

              return (
                <label key={column.id} className={css.checkboxItem}>
                  <input
                    type="checkbox"
                    checked={visibleColumns.includes(column.id)}
                    disabled={isRequired}
                    onChange={() => toggleColumn(column.id)}
                  />
                  <span>{column.label}</span>
                </label>
              );
            })}
          </div>
        </div>

        <div className={css.toolbarBlock}>
          <div className={css.toolbarTitle}>Группировка</div>
          <div className={css.checkboxList}>
            {DEAL_GROUP_OPTIONS.map((option) => (
              <label key={option.id} className={css.checkboxItem}>
                <input
                  type="checkbox"
                  checked={groupBy.includes(option.id)}
                  onChange={() => toggleGroup(option.id)}
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className={css.toolbarActions}>
          <Button variant="secondary" size="s" type="button" onClick={handleExportExcel}>
            Выгрузить в Excel
          </Button>
        </div>
      </div>

      <div className={css.container}>
        <table className={css.table}>
          <thead>
            <tr className={css.tr}>
              {columns.map((column) => (
                <th key={column.id} className={css.th}>
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              if (row.type === "group") {
                const totals = groupTotals(row.deals);

                return (
                  <tr key={`group-${row.groupKey}-${row.label}-${index}`} className={`${css.tr} ${css.groupRow}`}>
                    {columns.map((column, columnIndex) => {
                      if (columnIndex === 0) {
                        return (
                          <td
                            key={column.id}
                            className={css.td}
                            style={{ paddingLeft: `${12 + row.level * 16}px` }}
                          >
                            <div className={css.groupLabel}>
                              <span>
                                {groupOptionLabel(row.groupKey)}: {row.label}
                              </span>
                              <span className={css.groupMeta}>
                                {row.deals.length}{" "}
                                {row.deals.length === 1 ? "сделка" : row.deals.length < 5 ? "сделки" : "сделок"}
                              </span>
                            </div>
                          </td>
                        );
                      }

                      if (column.numeric && !column.noGroupSum) {
                        const value =
                          column.id === "opportunity"
                            ? totals.opportunity
                            : column.id === "profit"
                              ? totals.profit
                              : totals.actual_paid;
                        return (
                          <td key={column.id} className={css.td}>
                            {formatDealMoney(value)}
                          </td>
                        );
                      }

                      return (
                        <td key={column.id} className={css.td}>
                          —
                        </td>
                      );
                    })}
                  </tr>
                );
              }

              return (
                <tr key={`deal-${row.deal.link}-${index}`} className={css.tr}>
                  {columns.map((column, columnIndex) => (
                    <td
                      key={column.id}
                      className={css.td}
                      style={
                        columnIndex === 0
                          ? { paddingLeft: `${12 + row.level * 16}px` }
                          : undefined
                      }
                    >
                      {renderDealCell(column.id, row.deal)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default DealsDetailsView;
